import type { DataGridColumnOrderState, DataGridColumnPinningState } from '@ibsys/kit';
import type { ExpertReportSettings } from '@/entities/settings';
import { EXPERT_REPORT_PREFIX } from './constants';

/**
 * Раскладывает id колонок в том порядке, в котором они стоят в таблице у пользователя.
 * Повторяет порядок DataGrid: сначала закреплённые слева (в порядке закрепления), затем
 * незакреплённые в пользовательском порядке, затем закреплённые справа. Id, которых нет в
 * `columnOrder` (пользователь их не двигал), сохраняют исходный порядок и идут следом.
 *
 * @param ids - id колонок в исходном порядке объявления
 */
export const orderColumnIdsAsInTable = (
  ids: string[],
  columnOrder: DataGridColumnOrderState,
  columnPinning?: DataGridColumnPinningState,
): string[] => {
  const remaining = [...ids];
  const ordered: string[] = [];

  for (const id of columnOrder) {
    const index = remaining.indexOf(id);
    if (index !== -1) ordered.push(...remaining.splice(index, 1));
  }
  ordered.push(...remaining);

  const known = new Set(ids);
  const left = (columnPinning?.left ?? []).filter((id) => known.has(id));
  const right = (columnPinning?.right ?? []).filter((id) => known.has(id));
  const pinned = new Set([...left, ...right]);

  return [...left, ...ordered.filter((id) => !pinned.has(id)), ...right];
};

/**
 * Раскладывает настройки экспертных заключений в порядке таблицы: сами заключения — по позиции
 * их группы колонок, стандартные колонки и блоки внутри каждого — по позиции своих колонок.
 * Порядок массивов в запросе задаёт порядок колонок файла, поэтому перестановка колонок
 * заключения у пользователя доходит до выгрузки.
 */
export const orderExpertReportSettingsAsInTable = (
  settings: ExpertReportSettings[],
  columnOrder: DataGridColumnOrderState,
  columnPinning?: DataGridColumnPinningState,
): ExpertReportSettings[] => {
  const settingsById = new Map(settings.map((setting) => [`${EXPERT_REPORT_PREFIX}${setting.id}`, setting]));

  return orderColumnIdsAsInTable([...settingsById.keys()], columnOrder, columnPinning).map((groupId) => {
    const setting = settingsById.get(groupId) as ExpertReportSettings;
    const columnIdPrefix = `${groupId}_`;

    return {
      ...setting,
      columns: orderColumnIdsAsInTable(
        setting.columns.map((column) => `${columnIdPrefix}${column}`),
        columnOrder,
        columnPinning,
      ).map((columnId) => columnId.slice(columnIdPrefix.length)) as ExpertReportSettings['columns'],
      blockIds: orderColumnIdsAsInTable(
        setting.blockIds.map((blockId) => `${columnIdPrefix}block_${blockId}`),
        columnOrder,
        columnPinning,
      ).map((columnId) => Number(columnId.slice(`${columnIdPrefix}block_`.length))),
    };
  });
};
